import { Formik, useFormikContext } from 'formik';
import React, { FC, ReactElement, useState } from 'react';
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { CertificateAction } from "../../constants/certificateActions";
import Commands from '../../constants/commands';
import { HardwareSubCmds } from '../../constants/sub-commands';
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { TabSummary } from '../../models/TabSummary';
import { executeCommand } from '../../services/executeCommandService';
import { generateCertificate } from '../../services/securityPolicyService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { IAppState } from "../../store/types";
import { buildCertForGeneration, handleImportCertificateSubmit } from "../../utilities/buildCertForGeneration";
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from '../dialog/DialogContext';
import CertificateDetails from '../security-policies/CertificateDetails';
import GenerateAndUploadCertificateConfirmCard from "../security-policies/GenerateAndUploadCertificateConfirmCard";
import GenerateConfirmCard from '../security-policies/GenerateConfirmModal';
import GenerateRecreateCertificateCard from '../security-policies/GenerateRecreateCertificateCard';
import { ImportExportCertificateCard } from '../security-policies/ImportExportCertificateCard';
import UploadCertificateConfirmCard from '../security-policies/UploadCertificateConfirmCard';
import Tabs from '../tabs/tabs';
import AuditTab from './securityTabs/AuditTab';
import CertificateTab from './securityTabs/CertificateTab';
import GeneralTab from './securityTabs/GeneralTab';
import { ModalType } from './securityTabs/ModalType';

interface ControllerSecurityProps {
    reloadPage?: () => void
}

export const ControllerSecurity: FC<ControllerSecurityProps> = ({ reloadPage }) => {
    const { values } = useFormikContext<any>();
    const { translate } = useLocale()
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [busy, setBusy] = useState<boolean>(false);
    const dialog = useDialog();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [selectedTab, setSelectedTab] = useState(0);
    const [modalType, setModalType] = React.useState<ModalType>(null);
    const generateCertificateInitialValues = { id: 0, name: '', validFor: 1 };
    const uploadExportCertificateInitialValues = { certificatePassword: '' };
    const tabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.general), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.certificate), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.audit), badge: 0, showInitial: true, isActive: true },
    ];
    const [certToUploadStatus, setCertToUploadStatus] = useState<{ status: string, message: string }>({ status: '', message: '' });

    const tabContent: ReactElement[] = [
        <GeneralTab />,
        <CertificateTab setModalType={setModalType} certToUploadStatus={certToUploadStatus} setCertToUploadStatus={setCertToUploadStatus} reloadPage={reloadPage} />,
        <AuditTab controllerId={values.controllerId}/>
    ];

    const onTabChange = (index: number) => {
        setSelectedTab(index);
    }

    const onGenerateAndUploadCertificate = async () => {
        setBusy(true);
        setModalType(null);
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.Send_Generate_Upload,
                siteId: siteId,
                entityId: values.controllerId,
                controllerId: values.controllerId,
            }
            await executeCommand(command);
        } catch (error) {
            if (typeof error === 'object' && error !== null && 'status' in error && 'message' in error) {
                setCertToUploadStatus({ status: (error as any).status, message: (error as any).message });
            }
            dialog.pushError({ title: translate.byKey(TranslationKeys.sendCertificateToControllerError), message: getErrorMessage(error) });
        } finally {
            if (reloadPage) reloadPage();
            setModalType(null);
            setBusy(false);
        }
    }

    const onGenerateCertificate = async (values) => {
        setBusy(true);
        setModalType(null);
        try {
            const cert = buildCertForGeneration(values, 0, siteId, user.id);
            await generateCertificate(cert);
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.certificateGenerationError), message: getErrorMessage(error) });
        }
        setBusy(false);
    };

    const handleSendCertToController = async () => {
        setBusy(true);
        setModalType(null);
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.Send_Certificate,
                siteId: siteId,
                entityId: values.controllerId,
                controllerId: values.controllerId,
                certificateId: values.ksCertificateToUploadId
            }
            await executeCommand(command);
        } catch (error) {
            if (typeof error === 'object' && error !== null && 'status' in error && 'message' in error) {
                setCertToUploadStatus({ status: (error as any).status, message: (error as any).message });
            }
            dialog.pushError({ title: translate.byKey(TranslationKeys.sendCertificateToControllerError), message: getErrorMessage(error) });
        } finally {
            if (reloadPage) reloadPage();
            setModalType(null);
            setBusy(false);
        }
    }

    const getModalContent = (modalType: ModalType) => {
        switch (modalType) {
            case "details":
                return (
                    <CertificateDetails
                        onClose={() => setModalType(null)}
                        id={values.ksCertificateDetailsId} />
                );
            case "generateConfirm":
                return (
                    <GenerateConfirmCard
                        handleCancel={() => setModalType(null)}
                        handleConfirm={() => setModalType("generate")} />
                );
            case "uploadCertConfirm":
                return (
                    <UploadCertificateConfirmCard
                        handleCancel={() => setModalType(null)}
                        handleConfirm={handleSendCertToController}
                        isEc3Controller={(values.typeId != null && values.typeId == 150)}
                    />
                );
            case "generateAndUpload":
                return (
                    <GenerateAndUploadCertificateConfirmCard
                        handleCancel={() => setModalType(null)}
                        handleConfirm={onGenerateAndUploadCertificate}
                        isEc3Controller={(values.typeId != null && (values.typeId == 150 && values.tlsMode == 0))}
                    />
                );
            case "generate":
                return (
                    <Formik initialValues={generateCertificateInitialValues} onSubmit={onGenerateCertificate}>
                        <GenerateRecreateCertificateCard
                            type={CertificateAction.GENERATE}
                            title={translate.byKey(TranslationKeys.generateCertificate)}
                            onClose={() => setModalType(null)} />
                    </Formik>
                );
            case "import":
                return (
                    <Formik initialValues={uploadExportCertificateInitialValues} onSubmit={(values) => handleImportCertificateSubmit(values, setBusy, setModalType, toast.success, dialog, translate, siteId)}>
                        <ImportExportCertificateCard
                            type="import"
                            title={translate.byKey(TranslationKeys.importCertificate)}
                            onClose={() => setModalType(null)} />
                    </Formik>
                );
            default:
                return null;
        }
    }

    return <>
        <ActivityFrame busy={busy} className='h-full flex flex-col flex-grow bg-white' >
            <div className="h-full w-full overflow-hidden" >
                <Tabs
                    tabs={tabs}
                    onSelect={onTabChange}
                    selected={selectedTab}
                    add={false}
                    onAdd={() => { }}
                    isFlat={true}
                    className="w-full flex flex-col h-full"
                    showBorder={false}>
                    {tabContent[selectedTab]}
                </Tabs>
            </div>
        </ActivityFrame>
        <Modal
            isOpen={modalType !== null}
            onRequestClose={() => setModalType(null)}
            ariaHideApp={false}
            overlayClassName="flex w-screen fixed top-0 left-0 h-screen bg-opacity-50 bg-black"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            {getModalContent(modalType)}
        </Modal>
    </>
}
